import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Gift, CheckCircle } from "@phosphor-icons/react";
import { api, errMsg, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHero, Section, Loader } from "@/components/Blocks";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

export default function GiftCards() {
  const { user } = useAuth();
  const [f, setF] = useState({ amount: "", recipient_name: "", recipient_email: "", message: "" });
  const [busy, setBusy] = useState(false);
  const cents = Math.round(Number(f.amount) * 100);
  const buy = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/gift-cards/checkout", { amount_cents: cents, recipient_name: f.recipient_name, recipient_email: f.recipient_email, message: f.message, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  return (
    <>
      <PageHero eyebrow="Give the launch" title="Gift cards" sub="Send a Mission Control Trading gift card by email. It can be used toward any class ticket and has no expiry date." />
      <Section>
        <div className="mx-auto max-w-xl panel space-y-4 p-6" data-testid="giftcard-form">
          <Gift size={36} weight="duotone" className="text-[#E0A21B]" />
          {!user ? <p className="text-sm">Please <Link to="/login?next=/gift-cards" className="font-semibold underline" data-testid="giftcard-login-link">sign in</Link> to buy a gift card.</p> : (
            <>
              <label className="block text-sm">Amount (US$25 to US$2,000)<Input type="number" min="25" max="2000" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="giftcard-amount-input" /></label>
              <label className="block text-sm">Recipient name<Input value={f.recipient_name} onChange={(e) => setF({ ...f, recipient_name: e.target.value })} data-testid="giftcard-name-input" /></label>
              <label className="block text-sm">Recipient email<Input type="email" value={f.recipient_email} onChange={(e) => setF({ ...f, recipient_email: e.target.value })} data-testid="giftcard-email-input" /></label>
              <label className="block text-sm">Message (optional)<Textarea maxLength={300} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="giftcard-message-input" /></label>
              <button className="btn-gold w-full" disabled={busy || cents < 2500 || cents > 200000 || !f.recipient_name || !f.recipient_email} onClick={buy} data-testid="giftcard-buy-btn">{busy ? "Opening secure checkout…" : `Buy ${cents >= 2500 ? money(cents) : ""} gift card`}</button>
              <p className="text-xs text-[#475569]">Paid securely through Stripe. The code is emailed to the recipient right after payment. Gift cards have no cash value.</p>
            </>
          )}
        </div>
      </Section>
    </>
  );
}

export function GiftCardSuccess() {
  const [sp] = useSearchParams();
  const [d, setD] = useState(null);
  useEffect(() => {
    let n = 0, stop = false;
    const poll = async () => {
      try { const { data } = await api.get(`/gift-cards/status/${sp.get("session_id")}`); if (data.status === "paid") { setD(data); return; } } catch { /* keep polling */ }
      if (++n < 15 && !stop) setTimeout(poll, 2000); else setD({ status: "timeout" });
    };
    poll();
    return () => { stop = true; };
  }, [sp]);
  return (
    <Section>
      <div className="mx-auto max-w-xl panel p-8" data-testid="giftcard-result">
        {!d ? <Loader label="Confirming your payment" /> : d.status === "paid" ? (
          <><CheckCircle size={56} weight="fill" className="text-[#1E8E5A]" /><h1 className="mt-4 font-display text-4xl font-black uppercase" data-testid="giftcard-success-title">Gift card sent</h1><p className="mt-2 text-[#475569]">{money(d.amount_cents)} is on its way to {d.recipient_name} by email. You'll get a copy of the code too.</p></>
        ) : <p className="text-sm">Still confirming. Check your email in a minute.</p>}
      </div>
    </Section>
  );
}
